import { createClient } from "@supabase/supabase-js";

/**
 * The Supabase browser client.
 *
 * It is used for **one thing**: authentication - sign-in, social sign-in,
 * password reset, session persistence and token refresh. It never touches
 * media; that is Azure Blob over a SAS the API mints (docs/decisions.md #43).
 *
 * It is never used to query business data. Every read and write of companies,
 * sites, users, assessments and hazards goes through the Express API, which
 * holds the business logic both this console and the Flutter app share. If you
 * find yourself writing `supabase.from("...")` in a feature, that logic belongs
 * in the API instead.
 *
 * The anon key is designed to be public - it grants nothing on its own, because
 * every table is behind RLS and every policy requires a real session.
 */

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  // Fail loudly at module load. The alternative is a login form that looks fine
  // and fails with an opaque network error on submit.
  throw new Error(
    "Missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY. Copy .env.example to .env at the repo root."
  );
}

export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // Social sign-in returns to /auth/callback carrying `?code=`; this is what
    // exchanges it. Emailed links no longer arrive here at all - they carry a
    // `token_hash` redeemed explicitly on submit. See ResetPassword.tsx.
    detectSessionInUrl: true,
    // PKCE is here for the OAuth redirect, which is a real authorization-code
    // flow and the one place in this app where an intercepted code would
    // matter. It is the reason `supabaseNoPkce` below has to exist - do not
    // "simplify" by turning this off.
    flowType: "pkce",
    storageKey: "taskiq.auth",
  },
});

/**
 * A second client that exists for **one call**: sending the password-reset email.
 *
 * `resetPasswordForEmail` on a PKCE client sends a `code_challenge`. GoTrue then
 * records a flow state and prefixes the emailed `{{ .TokenHash }}` with `pkce_`,
 * producing a token whose only completion path is
 * `GET /auth/v1/verify` → auth code → `exchangeCodeForSession`. That is fatal
 * here twice over: the GET **is** the redemption, so it cannot be deferred to a
 * form submit the way the whole email design requires, and the code verifier
 * lives in the localStorage of the browser that started the flow, so reading the
 * mail on a different device could never work. `verifyOtp` cannot redeem such a
 * token at all, which is why every reset link failed as "expired" seconds after
 * being sent.
 *
 * `flowType` is fixed at construction - `resetPasswordForEmail` takes only
 * `redirectTo` and `captchaToken` - so a second client is the only way to send
 * that one request without PKCE while keeping it for OAuth.
 *
 * It holds no session and never will. The token it causes to be emailed is
 * redeemed on the **main** client above, so the session it produces is persisted
 * and refreshed like any other.
 *
 * The distinct `storageKey` is load-bearing: auth-js counts instances per storage
 * key and warns about "Multiple GoTrueClient instances" only when one is reused.
 *
 * **Nothing else may use this client.** Anything that needs a session belongs on
 * `supabase`.
 */
export const supabaseNoPkce = createClient(url, anonKey, {
  auth: {
    flowType: "implicit",
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
    storageKey: "taskiq.auth.nopkce",
  },
});

/**
 * The current access token, or null.
 *
 * Reads from the SDK rather than caching, so it always reflects the latest
 * refresh. `getSession()` is a local read once the session is loaded - it does
 * not round-trip - so calling it per request is cheap.
 */
export async function currentAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}
